import { build_prerender_json_ld } from "./jsonld"
import { url_for_sanity_image } from "./image_url"
import type { BlogBootstrap } from "./types"

const DEFAULT_TITLE = "MarginWiz"
const DEFAULT_DESCRIPTION =
  "The first AI platform for CPG that lets you fight deductions in a few clicks by pulling all your data into one place."

const OG_SITE_NAME = "MarginWiz"

/** Site-wide fallback share card (1200x630). Used whenever a page has no image of its own, so
 * LinkedIn/X/Slack never render an imageless card. Posts override it via `seo.ogImage`/`mainImage`. */
const DEFAULT_OG_IMAGE_PATH = "/img/og-default.png"

export function site_origin(): string {
  return (import.meta.env.VITE_SITE_ORIGIN as string | undefined) || "https://www.marginwiz.com"
}

/** Social crawlers require an absolute URL — a root-relative `/img/...` is silently dropped. */
function absolute_og_image(image: string | null, origin: string): string {
  if (!image) {
    return `${origin}${DEFAULT_OG_IMAGE_PATH}`
  }
  return image.startsWith("http") ? image : `${origin}${image.startsWith("/") ? "" : "/"}${image}`
}

export interface PrerenderHeadResult {
  title: string
  description: string
  ogUrl: string
  /** Always set — falls back to the site default card. Absolute URL. */
  ogImage: string
  canonicalUrl: string
  ogType: "website" | "article"
  ogSiteName: string
  jsonLd: Record<string, unknown>
}

function og_type_for_path(path: string, blogBootstrap: BlogBootstrap): "website" | "article" {
  if (path.startsWith("/blog/") && blogBootstrap?.kind === "post" && blogBootstrap.post) {
    return "article"
  }
  return "website"
}

export function prerender_head_for_path(urlPath: string, blogBootstrap: BlogBootstrap): PrerenderHeadResult {
  const origin = site_origin().replace(/\/$/, "")
  const head = head_for_path(urlPath, blogBootstrap)
  return { ...head, ogImage: absolute_og_image(head.ogImage, origin) }
}

/** Per-path head values. `ogImage` may be null here; `prerender_head_for_path` fills the default. */
function head_for_path(
  urlPath: string,
  blogBootstrap: BlogBootstrap,
): Omit<PrerenderHeadResult, "ogImage"> & { ogImage: string | null } {
  const origin = site_origin().replace(/\/$/, "")
  const path = urlPath.split("?")[0].replace(/\/$/, "") || "/"
  const ogUrl = path === "/" ? `${origin}/` : `${origin}${path}`
  const jsonLd = build_prerender_json_ld(origin, urlPath, blogBootstrap)
  const ogType = og_type_for_path(path, blogBootstrap)

  if (path === "/") {
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      ogUrl: `${origin}/`,
      ogImage: null,
      canonicalUrl: `${origin}/`,
      ogType,
      ogSiteName: OG_SITE_NAME,
      jsonLd,
    }
  }

  if (path === "/blog") {
    return {
      title: "Blog | MarginWiz",
      description: "Product updates, trade promotion tips, and how teams recover more deductions.",
      ogUrl: `${origin}/blog`,
      ogImage: null,
      canonicalUrl: `${origin}/blog`,
      ogType,
      ogSiteName: OG_SITE_NAME,
      jsonLd,
    }
  }

  if (path.startsWith("/blog/") && blogBootstrap?.kind === "post" && blogBootstrap.post) {
    const p = blogBootstrap.post
    const title = p.seo?.metaTitle || p.title
    const description = (p.seo?.metaDescription || p.excerpt || DEFAULT_DESCRIPTION).slice(0, 320)
    const ogFromSeo = url_for_sanity_image(p.seo?.ogImage ?? undefined)
    const ogFromMain = url_for_sanity_image(p.mainImage ?? undefined)
    return {
      title: `${title} | MarginWiz`,
      description,
      ogUrl,
      ogImage: ogFromSeo || ogFromMain,
      canonicalUrl: ogUrl,
      ogType,
      ogSiteName: OG_SITE_NAME,
      jsonLd,
    }
  }

  if (path.startsWith("/blog/")) {
    return {
      title: "Blog | MarginWiz",
      description: DEFAULT_DESCRIPTION,
      ogUrl,
      ogImage: null,
      canonicalUrl: ogUrl,
      ogType,
      ogSiteName: OG_SITE_NAME,
      jsonLd,
    }
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogUrl: `${origin}/`,
    ogImage: null,
    canonicalUrl: `${origin}/`,
    ogType,
    ogSiteName: OG_SITE_NAME,
    jsonLd,
  }
}
